import { Component, OnInit, OnDestroy, Output, EventEmitter, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { FeatureCollection } from 'src/app/core/models';
import { StoreComparison } from 'src/app/core/store/ranking/store-comparison';
import { CompetitiveAnalysisDetailsWizard } from '../competitive-analysis-card/competitive-analysis-details-wizard/competitive-analysis-details-wizard';
import { AuthorizationAwareComponent } from 'src/app/core/authorization-aware-component';
import { Store as NgrxStore } from '@ngrx/store';
import { AppState } from 'src/app/app-state';
import { RegionType } from 'src/app/core/location/region-type.enum';
import { ComparisonType } from 'src/app/core/store/ranking/comparison-type.enum';
import { DetailState, selectDetailState } from 'src/app/detail/detail-state';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import * as _ from 'lodash';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
import { StoreRanking } from 'src/app/core/store/ranking/store-ranking';
import { KeyIndicatorType } from 'src/app/core/user-preferences/key-indicator-type.enum';

export interface RankingFilters {
  storeType: string;
  driveTime: string;
  number: number;
  comparisonType: ComparisonType;
}

@Component({
  standalone: false,
  selector: 'mtn-store-ranking-filters',
  templateUrl: './store-ranking-filters.component.html',
  styleUrls: ['./store-ranking-filters.component.scss']
})
export class StoreRankingFiltersComponent extends AuthorizationAwareComponent implements OnInit, OnDestroy, OnChanges {

  @Output() filtersChanged = new EventEmitter<RankingFilters>();
  @Input() comparisonRequest: StoreComparison;
  @Input() keyIndicator: KeyIndicatorType = KeyIndicatorType.SALES_VOLUME;

  private readonly VALIDATORS_DRIVE_TIME = [Validators.min(1), Validators.max(20)];
  private readonly VALIDATORS_RING = [Validators.min(1), Validators.max(10)];
  private subscriptions: Subscription[] = [];

  comparison: StoreComparison;
  selectedStoreType: string = 'All Grocery Stores';
  selectedDriveTime: string = 'Drive time';
  selectedNumber: number = 7;
  form = new UntypedFormGroup({});
  marketArea: FeatureCollection;
  RegionType = RegionType;
  ComparisonType = ComparisonType;
  storeTypeDropdownOpen: boolean = false;
  driveTimeDropdownOpen: boolean = false;

  // Rankings data
  rankings: StoreRanking[] = [];
  currentStoreRanking: StoreRanking = null;
  isLoading: boolean = false;

  // Store data
  currentStore: any = null;

  // Initialization flag for data loading
  private dataInitialized: boolean = false;

  storeTypeOptions: string[] = [
    'All Grocery Stores',
    'Same Banner',
    'Same Fit Category'
  ];

  driveTimeOptions: string[] = [
    'Drive time',
    'Ring'
  ];

  constructor(
    protected ngrxStore: NgrxStore<AppState>,
    private hitTrackingService: HitTrackingService
  ) {
    super(ngrxStore);

    this.form = new UntypedFormGroup({
      comparisonType: new UntypedFormControl(ComparisonType.ALL, [Validators.required]),
      driveTimeMinutes: new UntypedFormControl(7, this.VALIDATORS_DRIVE_TIME),
      region: new UntypedFormControl(RegionType.DRIVE_TIME, [Validators.required]),
      ringMiles: new UntypedFormControl(3, this.VALIDATORS_RING)
    });
  }

  onAuthorizationChange(): void {
  }

  onAuthorizationInit(): void {
  }

  ngOnInit(): void {
    this.subscribeToDetailState();
    this.subscribeToFormChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keyIndicator'] && !changes['keyIndicator'].firstChange) {
      this.fetchStoreRankings();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private subscribeToDetailState(): void {
    const sub = selectDetailState(this.ngrxStore)
      .subscribe({
          next: (state: DetailState) => {
            this.marketArea = state.marketArea;
            this.currentStore = state.store;

            // Get comparison from state
            if (!this.comparison) {
              if (this.comparisonRequest) {
                this.comparison = _.find([...state.defaultComparisons, ...state.customComparisons],
                  (comparison: StoreComparison) => this.comparisonRequest.equals(comparison, true));
              } else {
                this.comparison = state.defaultComparisons?.length > 0 ? state.defaultComparisons[0] : null;
              }
            }

            // Initialize once we have all required data
            if (!this.dataInitialized && this.currentStore && this.marketArea) {
              this.dataInitialized = true;
              this.initializeFromComparison();
              this.fetchStoreRankings();
            }
          },
          error: (error) => {
            console.error("Error in DetailState subscription:", error);
          }
        });
    this.subscriptions.push(sub);
  }

  private initializeFromComparison(): void {
    if (this.comparison) {
      // Set form values from comparison
      this.form.patchValue({
        comparisonType: this.comparison.comparisonType || ComparisonType.ALL,
        driveTimeMinutes: this.comparison.driveTimeMinutes || 7,
        region: this.comparison.region || RegionType.DRIVE_TIME,
        ringMiles: this.comparison.ringMiles || 3
      }, { emitEvent: false });

      // Update dropdown display values
      this.updateSelectedStoreType(this.form.get('comparisonType').value);
      this.updateSelectedDriveTime(this.form.get('region').value);
      this.selectedNumber = this.form.get('region').value === RegionType.RING
        ? this.form.get('ringMiles').value
        : this.form.get('driveTimeMinutes').value;
    }
  }

  private subscribeToFormChanges(): void {
    // Comparison type changes
    this.subscriptions.push(
      this.form.get('comparisonType').valueChanges.subscribe(value => {
        this.updateSelectedStoreType(value);
        this.onFilterChange();
      })
    );

    // Region type changes
    this.subscriptions.push(
      this.form.get('region').valueChanges.subscribe(value => {
        this.updateSelectedDriveTime(value);
        this.onFilterChange();
      })
    );

    // Drive time changes with debounce
    this.subscriptions.push(
      this.form.get('driveTimeMinutes').valueChanges.pipe(debounceTime(600)).subscribe(value => {
        if (this.form.get('region').value === RegionType.DRIVE_TIME && value) {
          this.selectedNumber = value;
          this.onFilterChange();
        }
      })
    );

    // Ring miles changes with debounce
    this.subscriptions.push(
      this.form.get('ringMiles').valueChanges.pipe(debounceTime(600)).subscribe(value => {
        if (this.form.get('region').value === RegionType.RING && value) {
          this.selectedNumber = value;
          this.onFilterChange();
        }
      })
    );
  }

  private onFilterChange(): void {
    if (!this.dataInitialized) return;

    this.emitFilters();
    this.fetchStoreRankings();
    this.trackFilterChange('filter_change', 'reactive_form');
  }

  private updateSelectedStoreType(comparisonType: ComparisonType): void {
    switch (comparisonType) {
      case ComparisonType.ALL:
        this.selectedStoreType = 'All Grocery Stores';
        break;
      case ComparisonType.BANNER:
        this.selectedStoreType = 'Same Banner';
        break;
      case ComparisonType.FIT_CATEGORY:
        this.selectedStoreType = 'Same Fit Category';
        break;
      default:
        this.selectedStoreType = 'All Grocery Stores';
    }
  }

  private updateSelectedDriveTime(regionType: RegionType): void {
    switch (regionType) {
      case RegionType.DRIVE_TIME:
        this.selectedDriveTime = 'Drive time';
        this.selectedNumber = this.form.get('driveTimeMinutes').value || 7;
        break;
      case RegionType.RING:
        this.selectedDriveTime = 'Ring';
        this.selectedNumber = this.form.get('ringMiles').value || 3;
        break;
      case RegionType.MARKET_AREA:
        this.selectedDriveTime = 'Market Area';
        break;
      case RegionType.TRADE_AREA:
        this.selectedDriveTime = 'Trade Area';
        break;
      default:
        this.selectedDriveTime = 'Drive time';
    }
  }

  fetchStoreRankings(): void {
    // Remove the API call from filters component - it's now in scatter chart
  }

  getRankDisplayText(): string {
    return 'Ranking filters';
  }

  openComparisonDetailWizard(): void {
    const wizard = new CompetitiveAnalysisDetailsWizard();
    wizard.model = {
      comparison: this.comparison,
      isRestrictedLicenseOverlayEnabled: !this.hasStandardLicense,
      marketArea: this.marketArea
    };

    this.comparison = wizard.model.comparison;
    this.marketArea = wizard.model.marketArea;
  }

  toggleStoreTypeDropdown(): void {
    this.storeTypeDropdownOpen = !this.storeTypeDropdownOpen;
    this.driveTimeDropdownOpen = false;
  }

  toggleDriveTimeDropdown(): void {
    this.driveTimeDropdownOpen = !this.driveTimeDropdownOpen;
    this.storeTypeDropdownOpen = false;
  }

  selectStoreType(option: string): void {
    this.selectedStoreType = option;
    this.storeTypeDropdownOpen = false;

    // Map display option to ComparisonType
    let comparisonType: ComparisonType;
    switch (option) {
      case 'Same Banner':
        comparisonType = ComparisonType.BANNER;
        break;
      case 'Same Fit Category':
        comparisonType = ComparisonType.FIT_CATEGORY;
        break;
      default:
        comparisonType = ComparisonType.ALL;
    }

    this.form.get('comparisonType').setValue(comparisonType);
    this.trackFilterChange('store_type', option);
  }

  selectDriveTime(option: string): void {
    this.selectedDriveTime = option;
    this.driveTimeDropdownOpen = false;

    let regionType: RegionType;
    switch (option) {
      case 'Ring':
        regionType = RegionType.RING;
        this.selectedNumber = this.form.get('ringMiles').value || 3;
        break;
      case 'Market Area':
        regionType = RegionType.MARKET_AREA;
        break;
      case 'Trade Area':
        regionType = RegionType.TRADE_AREA;
        break;
      default:
        regionType = RegionType.DRIVE_TIME;
        this.selectedNumber = this.form.get('driveTimeMinutes').value || 7;
    }

    this.form.get('region').setValue(regionType);
    this.trackFilterChange('drive_time_type', option);
  }

  incrementNumber(): void {
    const max = this.selectedDriveTime === 'Ring' ? 10 : 20;
    if (this.selectedNumber < max) {
      this.selectedNumber++;
      this.updateFormNumber();
      this.trackFilterChange('number_increment', this.selectedNumber);
    }
  }

  decrementNumber(): void {
    if (this.selectedNumber > 1) {
      this.selectedNumber--;
      this.updateFormNumber();
      this.trackFilterChange('number_decrement', this.selectedNumber);
    }
  }

  onNumberChange(value: number): void {
    const max = this.selectedDriveTime === 'Ring' ? 10 : 20;
    if (value >= 1 && value <= max) {
      this.selectedNumber = value;
    } else if (value < 1) {
      this.selectedNumber = 1;
    } else if (value > max) {
      this.selectedNumber = max;
    }
    this.updateFormNumber();
    this.trackFilterChange('number_change', this.selectedNumber);
  }

  private updateFormNumber(): void {
    if (this.form.get('region').value === RegionType.RING) {
      this.form.get('ringMiles').setValue(this.selectedNumber);
    } else {
      this.form.get('driveTimeMinutes').setValue(this.selectedNumber);
    }
  }

  private trackFilterChange(_filterType: string, _filterValue: any): void {
    console.log('trackFilterChange called with:', _filterType, _filterValue);
    console.log('currentStore:', this.currentStore);
    console.log('hitTrackingService:', this.hitTrackingService);
    
    if (this.currentStore && this.hitTrackingService) {
      const metadata: any = {
        storeId: this.currentStore.uuid,
        storeName: this.currentStore.name,
        storeType: this.selectedStoreType,
        region: this.selectedDriveTime,
      };
      console.log("tracking call here ", metadata);
      if (this.selectedDriveTime === 'Drive time') {
        metadata['driveTimeMinutes'] = this.selectedNumber;
      } else if (this.selectedDriveTime === 'Ring') {
        metadata['ringMiles'] = this.selectedNumber;
      }

      this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.STORE_SALES_PERFORMANCE_PANEL,
        metadata,
        USER_TRACKING_ACTIONS.RBS_FILTER_CHANGED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_FILTER_CHANGED],
        true
      ).subscribe({
        next: (response) => {
          console.log('Tracking successful:', response);
        },
        error: (error) => {
          console.error('Tracking failed:', error);
        }
      });
    } else {
      console.log('Tracking not called - missing dependencies:', {
        currentStore: !!this.currentStore,
        hitTrackingService: !!this.hitTrackingService
      });
    }
  }

  private emitFilters(): void {
    const filters: RankingFilters = {
      storeType: this.selectedStoreType,
      driveTime: this.selectedDriveTime,
      number: this.selectedNumber,
      comparisonType: this.form.get('comparisonType').value
    };

    this.filtersChanged.emit(filters);
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-dropdown')) {
      this.storeTypeDropdownOpen = false;
      this.driveTimeDropdownOpen = false;
    }
  }
}
